/**
 * STEP GROWTH-B2 · FAN BOOST wizard  (STEP GROWTH-B2-FIX applied)
 *
 * REUSE ONLY — no new engine:
 *   · Existing short_clips  → /api/seller/short-clips (thumbnail + video_url + duration_sec)
 *   · Existing video pipeline → /api/seller/short-clip-media (300MB · MP4/etc.)
 *   · Existing image pipeline → /api/uploads (5MB · JPG/PNG/WEBP)
 *   · Existing wallet         → /api/seller/wallet
 *   · Existing Kakao login    (public landing page reuses it)
 *
 * Fixed vs. previous revision:
 *   · STEP 3 → 4 blocker: ImageUpload actually uses value/onChange (not
 *     onUpload) — creative state now updates correctly, so canNext[3] flips.
 *   · Existing short-form list: fetched directly from /seller/short-clips
 *     (not iterated per-clip publish_jobs which was empty on most sellers).
 *   · Creative label changed to "광고 소재 업로드" · video-first.
 *   · 9:16 mobile preview panel rendered as soon as a creative is chosen.
 *   · STEP 5 shows wallet balance + estimated cost + insufficient warning.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  X, ChevronLeft, ChevronRight, MessageCircle, MessageSquare,
  Instagram, Music, Film, Upload as UploadIcon, Check, ExternalLink,
  Copy, Wallet, PlayCircle, Loader2, AlertCircle, ImageIcon, Eye, ArrowDown,
} from 'lucide-react';
import api from '@/lib/api';

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', Icon: Instagram },
  { value: 'tiktok',    label: 'TikTok',    Icon: Music },
];
const MODES = [
  { value: 'comment', label: '댓글형',   headline: '지정 댓글 → 자동 DM → 단골 등록',
    detail:  '광고에 지정된 댓글을 남기면 자동 DM을 보내 단골 등록으로 연결합니다.',
    example: ['"라이브모아" 댓글', '자동 DM', '단골 등록'], Icon: MessageCircle },
  { value: 'message', label: '메시지형', headline: '광고에서 메시지 → 자동 응답 → 단골 등록',
    detail:  '광고에서 메시지 대화를 시작한 고객을 자동 응답으로 단골 등록에 연결합니다.',
    example: ['메시지 보내기', 'DM 대화', '단골 등록'], Icon: MessageSquare },
];
// STEP GROWTH-B2-FIX-4 · default templates loaded when seller first picks a mode.
const IC_PRESETS = {
  comment: {
    ad_prompt:       '"라이브모아"라고 댓글을 남겨주세요! 다음 LIVE 알림을 보내드려요.',
    comment_keyword: '라이브모아',
    auto_reply_text: '감사합니다 😊\n아래 버튼을 눌러 단골 등록하고 다음 LIVE 알림을 받아보세요.',
    cta_label:       '단골 등록하기',
  },
  message: {
    ad_prompt:          '라이브 특가와 다음 방송 소식을 받아보세요.',
    message_start_text: '다음 LIVE 알림을 받고 싶어요.',
    auto_reply_text:    '안녕하세요 👋\n아래 버튼을 눌러 단골 등록하고 다음 LIVE 알림을 받아보세요.',
    cta_label:          '단골 등록하기',
  },
};
// "전국" is stored as a distinct region-value in the campaign's targeting.regions.
// UX rule (spec §4): 전국 and individual regions are mutually exclusive.
const NATIONWIDE = '전국';
const LOCAL_REGIONS = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];
const REGIONS = [NATIONWIDE, ...LOCAL_REGIONS];
const PLATFORM_FILTERS = [
  { value: 'all',       label: '전체' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok',    label: 'TikTok' },
  { value: 'youtube',   label: 'YouTube' },
];
const CATEGORIES = ['패션', '뷰티', '푸드', '리빙', '테크', '유아동', '반려동물', '취미', '스포츠'];
const GENDERS = [{ v: 'any', l: '전체' }, { v: 'female', l: '여성' }, { v: 'male', l: '남성' }];

const won = (n) => (n == null ? '-' : new Intl.NumberFormat('ko-KR').format(Number(n) || 0) + '원');
const fmtDate = (d) => (d ? new Date(d).toLocaleString('ko-KR') : '-');
const fmtDur  = (s) => (s == null ? '' : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`);

const STATUS_LABEL = {
  draft: '초안', pending: '승인대기', approved: '승인', running: '진행중',
  rejected: '반려', paused: '일시정지', ended: '종료',
};
const STATUS_TONE = {
  draft: 'bg-gray-500/10 text-gray-500',
  pending: 'bg-amber-500/10 text-amber-600',
  approved: 'bg-emerald-500/10 text-emerald-600',
  running: 'bg-emerald-500/10 text-emerald-600',
  rejected: 'bg-red-500/10 text-red-600',
  paused: 'bg-purple-500/10 text-purple-600',
  ended: 'bg-black/10 text-gray-600',
};

const buildLandingUrl = (sellerId, campaignId) =>
  `${window.location.origin}/g/${sellerId}?campaign=${campaignId}`;

/* ============================================================ */
/* 9:16 mobile-style creative preview (video or image)          */
/* ============================================================ */
// video URL classification · REUSE short_clips fields only.
// Includes our own /api/seller/short-clip-media/{id} route which serves MP4
// bytes with proper Content-Type but no .ext suffix (STEP GROWTH-B3-FIX-1).
const VIDEO_EXT = /\.(mp4|mov|webm|m3u8|m4v)(\?|$)/i;
const looksLikeDirectVideo = (u) =>
  !!u && (VIDEO_EXT.test(u) || /\/short-clip-media\//i.test(u));

// Platform embed detection · maps to <iframe> (real short_clip playback).
// Ordered from most-specific to least; each returns an embed-safe URL when
// the input can play, or null when it cannot.
function toEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  // YouTube embed (already in /embed/ form) — accept as-is.
  const yt = url.match(/(?:youtube\.com\/embed\/|youtube\.com\/shorts\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?playsinline=1`;
  // TikTok player iframe (v1) — official embed.
  const ttPlayer = url.match(/tiktok\.com\/player\/v1\/(\d+)/);
  if (ttPlayer) return url;                                       // already embed-safe
  const ttPage = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  if (ttPage) return `https://www.tiktok.com/player/v1/${ttPage[1]}?music_info=1&description=1`;
  const ttShort = url.match(/(?:vm|vt)\.tiktok\.com\/([A-Za-z0-9]+)/);
  if (ttShort) return null;                                       // short-link · needs unfurl · skip
  // Instagram reel · use official embed endpoint (no login required, iframe-friendly).
  const ig = url.match(/instagram\.com\/(?:reel|p|tv)\/([A-Za-z0-9_-]+)/);
  if (ig) return `https://www.instagram.com/reel/${ig[1]}/embed`;
  return null;
}

export function CreativePreview({ media_type, video_url, thumbnail_url, embed_url, source_url, small = false }) {
  const w = small ? 'w-32' : 'w-full max-w-[240px]';
  const wrap = `${w} aspect-[9/16] rounded-xl overflow-hidden bg-black relative mx-auto`;

  // Empty state
  if (!video_url && !thumbnail_url && !embed_url) {
    return (
      <div className={`${w} aspect-[9/16] rounded-xl bg-black/5 dark:bg-white/10 flex flex-col items-center justify-center text-gray-400`} data-testid="fanboost-preview-empty">
        <ImageIcon size={22} className="mb-1" />
        <p className="text-[10px]">소재 미선택</p>
      </div>
    );
  }

  // 1. Direct media file → real HTML5 <video>
  if (media_type === 'video' && looksLikeDirectVideo(video_url)) {
    return (
      <div className={wrap} data-testid="fanboost-preview-video">
        <video src={video_url} poster={thumbnail_url || undefined}
          controls playsInline preload="metadata"
          className="w-full h-full object-contain bg-black" />
      </div>
    );
  }

  // 2. Platform embed (YouTube / TikTok / Instagram) → <iframe> · REUSE embed_url or derive
  const embedTarget = toEmbedUrl(embed_url) || toEmbedUrl(video_url) || toEmbedUrl(source_url);
  if (embedTarget) {
    return (
      <div className={wrap} data-testid="fanboost-preview-embed">
        <iframe
          src={embedTarget}
          title="creative preview"
          className="w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  // 3. Fallback · thumbnail only · legacy short_clips with no playable source.
  const src = thumbnail_url || video_url;
  return (
    <div className="flex flex-col items-center gap-1" data-testid="fanboost-preview-image">
      <div className={wrap}>
        {src ? <img src={src} alt="creative preview" className="w-full h-full object-cover" />
             : <div className="w-full h-full flex items-center justify-center text-gray-400"><ImageIcon size={22}/></div>}
      </div>
      <p className="text-[10px] text-amber-600 dark:text-amber-400 font-bold" data-testid="fanboost-preview-nosource">
        <AlertCircle size={10} className="inline mr-0.5"/> 원본 영상 연결 필요
      </p>
    </div>
  );
}

/* ============================================================ */
/* Example flow modal — helps sellers understand each ad mode.  */
/* Illustrative only · does not claim platform capability.      */
/* ============================================================ */
function ExampleFlow({ open, mode, onClose }) {
  if (!open) return null;
  const isComment = mode === 'comment';
  const steps = isComment
    ? [
        { kind: 'ad',      text: '"라이브모아"라고\n댓글 남겨주세요!' },
        { kind: 'comment', text: '라이브모아' },
        { kind: 'dm',      text: '감사합니다 😊\n다음 LIVE 알림을 받아보세요.' },
        { kind: 'cta',     text: '단골 등록하기' },
        { kind: 'moa',     text: 'LIVEMOA\n카카오 로그인 → 단골 등록' },
      ]
    : [
        { kind: 'ad',      text: '라이브 특가와\n다음 방송 소식을 받아보세요.' },
        { kind: 'cta',     text: '메시지 보내기' },
        { kind: 'channel', text: 'Instagram/TikTok 등\n지원되는 메시지 채널' },
        { kind: 'dm',      text: '안녕하세요 👋\n다음 LIVE 알림을 받아보시겠어요?' },
        { kind: 'cta',     text: '단골 등록하기' },
        { kind: 'moa',     text: 'LIVEMOA\n카카오 로그인 → 단골 등록' },
      ];
  const iconOf = (k) => ({
    ad: <Film size={18} className="text-white" />,
    comment: <MessageCircle size={18} className="text-white" />,
    dm: <MessageSquare size={18} className="text-white" />,
    cta: <PlayCircle size={18} className="text-white" />,
    channel: <MessageSquare size={18} className="text-white" />,
    moa: <Check size={18} className="text-white" />,
  }[k]);
  const bgOf = (k) => ({
    ad: 'bg-[#111]',
    comment: 'bg-blue-500',
    dm: 'bg-[#FF3B30]',
    cta: 'bg-emerald-500',
    channel: 'bg-purple-500',
    moa: 'bg-black',
  }[k]);
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="fanboost-example-modal">
      <div className="w-full max-w-md bg-white dark:bg-[#141414] rounded-2xl overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-black/5 dark:border-white/10">
          <h4 className="font-black text-base">{isComment ? '댓글형 광고 예시' : '메시지형 광고 예시'}</h4>
          <button onClick={onClose} className="text-gray-400" data-testid="fanboost-example-close"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-1.5">
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div className={`flex items-start gap-3 p-3 rounded-xl ${bgOf(s.kind)} text-white`}>
                <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">{iconOf(s.kind)}</div>
                <p className="text-sm font-bold whitespace-pre-line leading-snug">{s.text}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="flex justify-center py-0.5"><ArrowDown size={14} className="text-gray-300" /></div>
              )}
            </React.Fragment>
          ))}
        </div>
        <p className="px-5 pb-4 text-[10px] text-gray-400 leading-relaxed">
          실제 광고 동작과 사용 가능한 기능은 연결된 광고 플랫폼 및 승인된 권한에 따라 달라질 수 있습니다.
        </p>
      </div>
    </div>
  );
}

/* ============================================================ */
/* Wizard modal                                                  */
/* ============================================================ */
function FanBoostWizard({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('comment');
  const [platform, setPlatform] = useState('instagram');

  // Creative kind: 'short_clip' (existing) | 'upload' (new video/image)
  const [creativeKind, setCreativeKind] = useState('short_clip');
  const [shortClips, setShortClips] = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [selectedClip, setSelectedClip] = useState(null);
  // STEP GROWTH-B2-FIX-2 · platform filter on the existing-short-clip list.
  const [clipPlatformFilter, setClipPlatformFilter] = useState('all');

  // Upload state
  const [uploaded, setUploaded] = useState(null); // {kind, ref_id, video_url, thumbnail_url, media_type}
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [targeting, setTargeting] = useState({
    regions: [], age_min: 20, age_max: 45, genders: ['any'], categories: [],
  });
  const [budget, setBudget] = useState(100000);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [wallet, setWallet] = useState(null);
  // STEP GROWTH-B2-FIX-4 · interaction_config state + example modal
  const [interactionConfig, setInteractionConfig] = useState({ ...IC_PRESETS.comment });
  const [exampleMode, setExampleMode] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setMode('comment'); setPlatform('instagram');
    setCreativeKind('short_clip'); setSelectedClip(null); setUploaded(null);
    setTargeting({ regions: [], age_min: 20, age_max: 45, genders: ['any'], categories: [] });
    setBudget(100000); setStartAt(''); setEndAt(''); setTitle('');
    setSaving(false); setUploading(false);
    setInteractionConfig({ ...IC_PRESETS.comment });
    setExampleMode(null);
  }, [open]);

  // Load preset when mode changes — but preserve edits if user already typed.
  useEffect(() => {
    if (!open) return;
    setInteractionConfig((prev) => {
      const preset = IC_PRESETS[mode] || {};
      // Detect whether prev is the OTHER mode's default and swap.  If user
      // has custom content we keep whatever fields are still relevant.
      const other = mode === 'comment' ? IC_PRESETS.message : IC_PRESETS.comment;
      const isDefault = Object.keys(other).every((k) => prev[k] === other[k]);
      return isDefault ? { ...preset } : { ...preset, ...prev };
    });
  }, [mode, open]);

  /* ---- STEP 3 · load existing short_clips ---- */
  useEffect(() => {
    if (!open || step !== 3 || creativeKind !== 'short_clip') return;
    (async () => {
      setClipsLoading(true);
      try {
        const r = await api.get('/seller/short-clips');
        setShortClips(r.data?.items || []);
      } catch { setShortClips([]); }
      setClipsLoading(false);
    })();
  }, [open, step, creativeKind]);

  /* ---- STEP 5 · wallet snapshot ---- */
  useEffect(() => {
    if (!open || step !== 5) return;
    (async () => {
      try {
        const r = await api.get('/seller/wallet');
        setWallet(r.data);
      } catch { setWallet(null); }
    })();
  }, [open, step]);

  if (!open) return null;

  /* ---- Creative pointer derivation ---- */
  const creative_source =
    creativeKind === 'short_clip' && selectedClip
      ? {
          kind: 'short_clip',
          ref_id: selectedClip.clip_id,
          platform,
          video_url: selectedClip.video_url,
          thumbnail_url: selectedClip.thumbnail_url,
          embed_url: selectedClip.embed_url || null,
          source_url: selectedClip.source_url || null,
          media_type: 'video',
        }
      : creativeKind === 'upload' && uploaded
        ? uploaded
        : null;

  const interactionValid = (() => {
    const ic = interactionConfig || {};
    const common = ic.ad_prompt && ic.auto_reply_text && ic.cta_label;
    if (mode === 'comment') return !!(common && ic.comment_keyword);
    if (mode === 'message') return !!(common && ic.message_start_text);
    return false;
  })();

  const canNext = {
    1: !!mode,
    2: !!platform,
    3: !!creative_source && interactionValid,
    4: true,
    5: !!budget && !!startAt && !!endAt && new Date(endAt) > new Date(startAt),
    6: true,
  }[step];

  /* ---- Upload handlers (video preferred · image fallback) ---- */
  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isVideo && !isImage) {
      toast.error('영상 또는 이미지 파일을 선택해주세요.');
      return;
    }
    setUploading(true);
    try {
      if (isVideo) {
        // REUSE existing short-clip video pipeline (300MB · MP4/MOV/WEBM)
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/seller/short-clip-media', fd);
        const { media_id, url } = res.data || {};
        const bearer = 'session';   // url is /api/seller/short-clip-media/{id}, auth handled by axios interceptor
        const full = url.startsWith('http') ? url : `${window.location.origin}${url}`;
        setUploaded({
          kind: 'short_clip_media',
          ref_id: media_id,
          platform,
          video_url: full,
          media_type: 'video',
        });
        toast.success('영상이 업로드되었습니다.');
      } else {
        // REUSE existing image pipeline (5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast.error('이미지는 5MB 이하로 업로드해주세요.');
          setUploading(false);
          return;
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', 'broadcast');
        const res = await api.post('/uploads', fd);
        const url = res.data?.url;
        const file_id = res.data?.file_id;
        setUploaded({
          kind: 'upload',
          ref_id: file_id,
          platform,
          thumbnail_url: url,
          media_type: 'image',
        });
        toast.success('이미지가 업로드되었습니다.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || '업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /* ---- Estimated cost (INTERNAL only · no real spend) ---- */
  const estCost = Number(budget) || 0;
  const walletBalance = wallet ? Number(wallet.balance || 0) : null;
  const insufficient = walletBalance != null && walletBalance < estCost;

  const save = async (submit = false) => {
    if (saving) return;
    setSaving(true);
    try {
      const created = await api.post('/seller/growth/fan-boost', {
        title:            title || `${PLATFORMS.find(p=>p.value===platform)?.label} ${MODES.find(m=>m.value===mode)?.label} 캠페인`,
        platform,
        mode,
        creative_source,
        interaction_config: interactionConfig,
        targeting,
        budget_krw:       Number(budget),
        start_at:         new Date(startAt).toISOString(),
        end_at:           new Date(endAt).toISOString(),
      });
      const ad_id = created.data?.campaign?.ad_id;
      if (!ad_id) throw new Error('생성 실패');
      if (submit) await api.post(`/seller/growth/fan-boost/${ad_id}/submit`);
      toast.success(submit ? '승인 요청이 접수되었습니다.' : '초안이 저장되었습니다.');
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const StepHeader = ({ n, label }) => (
    <div className={`flex items-center gap-2 text-xs ${step >= n ? 'text-black dark:text-white font-bold' : 'text-gray-400'}`}>
      <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] ${step >= n ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}>{n}</span>
      <span>{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="fanboost-wizard">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded-2xl">
        <div className="flex items-center justify-between p-4 border-b border-black/5 dark:border-white/10 sticky top-0 bg-white dark:bg-[#141414] z-10">
          <h3 className="text-lg font-black">단골 늘리기 광고 만들기</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-black dark:hover:text-white" data-testid="fanboost-close"><X size={18} /></button>
        </div>

        <div className="p-4 flex flex-wrap gap-2 border-b border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5">
          <StepHeader n={1} label="방식" />
          <ChevronRight size={12} className="text-gray-300 self-center" />
          <StepHeader n={2} label="플랫폼" />
          <ChevronRight size={12} className="text-gray-300 self-center" />
          <StepHeader n={3} label="광고 소재" />
          <ChevronRight size={12} className="text-gray-300 self-center" />
          <StepHeader n={4} label="타깃" />
          <ChevronRight size={12} className="text-gray-300 self-center" />
          <StepHeader n={5} label="예산·기간" />
          <ChevronRight size={12} className="text-gray-300 self-center" />
          <StepHeader n={6} label="미리보기" />
        </div>

        <div className="p-5 min-h-[300px]">
          {/* ===== STEP 1 · mode ===== */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {MODES.map((m) => (
                  <div key={m.value}
                    className={`text-left p-4 rounded-xl border-2 transition ${mode===m.value ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/5 dark:border-white/10 hover:border-black/20'}`}>
                    <button type="button" onClick={() => setMode(m.value)}
                      className="w-full text-left"
                      data-testid={`fanboost-mode-${m.value}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <m.Icon size={20} className="text-[#FF3B30]" />
                        <p className="font-black">{m.label}</p>
                        {mode===m.value && <Check size={16} className="text-[#FF3B30] ml-auto" />}
                      </div>
                      <p className="text-xs font-bold text-gray-600 dark:text-gray-300">{m.headline}</p>
                      <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{m.detail}</p>
                      <div className="flex flex-wrap items-center gap-1 mt-2 text-[10px] text-gray-500">
                        {m.example.map((step, i) => (
                          <React.Fragment key={i}>
                            <span className="px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10">{step}</span>
                            {i < m.example.length - 1 && <ChevronRight size={10} className="text-gray-300" />}
                          </React.Fragment>
                        ))}
                      </div>
                    </button>
                    <button type="button"
                      onClick={() => setExampleMode(m.value)}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#FF3B30] hover:underline"
                      data-testid={`fanboost-example-open-${m.value}`}>
                      <Eye size={12} /> 예시 보기
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[10px] text-gray-400 leading-relaxed">
                실제 광고 동작과 사용 가능한 기능은 연결된 광고 플랫폼 및 승인된 권한에 따라 달라질 수 있습니다.
              </p>
              <ExampleFlow open={!!exampleMode} mode={exampleMode} onClose={() => setExampleMode(null)} />
            </>
          )}

          {/* ===== STEP 2 · platform ===== */}
          {step === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PLATFORMS.map((p) => (
                <button key={p.value} onClick={() => setPlatform(p.value)}
                  className={`p-4 rounded-xl border-2 transition ${platform===p.value ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/5 dark:border-white/10 hover:border-black/20'}`}
                  data-testid={`fanboost-platform-${p.value}`}>
                  <p.Icon size={20} className="mb-2 mx-auto text-[#FF3B30]" />
                  <p className="font-black">{p.label}</p>
                </button>
              ))}
              <p className="col-span-full text-xs text-gray-400">YouTube는 단골 늘리기 광고의 초기 범위에서 제외됩니다.</p>
            </div>
          )}

          {/* ===== STEP 3 · Creative ===== */}
          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 md:items-start">
              <div className="space-y-3 min-w-0">
                <div className="flex gap-2">
                  <button onClick={() => setCreativeKind('short_clip')}
                    className={`flex-1 py-2 rounded-full text-sm font-bold ${creativeKind==='short_clip' ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}
                    data-testid="fanboost-creative-existing">
                    <Film size={14} className="inline mr-1" /> 기존 숏폼 선택
                  </button>
                  <button onClick={() => setCreativeKind('upload')}
                    className={`flex-1 py-2 rounded-full text-sm font-bold ${creativeKind==='upload' ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}
                    data-testid="fanboost-creative-upload">
                    <UploadIcon size={14} className="inline mr-1" /> 광고 소재 업로드
                  </button>
                </div>

                {creativeKind === 'short_clip' && (
                  <>
                    {/* Platform filter · REUSE short_clips.platform · no new API/DB */}
                    <div className="flex flex-wrap gap-1.5" data-testid="fanboost-clip-platform-filter">
                      {PLATFORM_FILTERS.map((p) => (
                        <button key={p.value} type="button"
                          onClick={() => { setClipPlatformFilter(p.value); setSelectedClip(null); }}
                          className={`px-3 py-1 rounded-full text-xs font-bold ${clipPlatformFilter === p.value ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10 hover:bg-black/10'}`}
                          data-testid={`fanboost-clip-filter-${p.value}`}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <div className="max-h-[320px] overflow-y-auto space-y-2 mt-2" data-testid="fanboost-existing-list">
                      {(() => {
                        const filtered = clipsLoading ? [] : shortClips.filter((c) => {
                          if (clipPlatformFilter === 'all') return true;
                          const p = (c.platform || '').toLowerCase();
                          return p === clipPlatformFilter;
                        });
                        if (clipsLoading) {
                          return <p className="text-xs text-gray-400 py-6 text-center"><Loader2 size={14} className="inline animate-spin mr-1" /> 불러오는 중…</p>;
                        }
                        if (filtered.length === 0) {
                          return (
                            <p className="text-xs text-gray-400 py-6 text-center">
                              {clipPlatformFilter === 'all'
                                ? '등록된 숏폼이 없습니다. 상단의 "광고 소재 업로드"를 사용해주세요.'
                                : `해당 플랫폼에 등록된 숏폼이 없습니다.`}
                            </p>
                          );
                        }
                        return filtered.map((c) => (
                          <label key={c.clip_id}
                            className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer ${selectedClip?.clip_id===c.clip_id ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/5 dark:border-white/10'}`}
                            data-testid={`fanboost-clip-${c.clip_id}`}>
                            <input type="radio" name="clip" checked={selectedClip?.clip_id===c.clip_id} onChange={() => setSelectedClip(c)} className="sr-only" />
                            <div className="w-12 h-20 rounded-md bg-black/10 dark:bg-white/10 overflow-hidden flex-shrink-0">
                              {c.thumbnail_url
                                ? <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover"
                                       onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                : <div className="w-full h-full flex items-center justify-center text-gray-400"><Film size={16}/></div>
                              }
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold truncate">{c.title || c.clip_id}</p>
                              <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 flex-wrap">
                                <span className="uppercase font-bold">{c.platform || 'LIVEMOA'}</span>
                                {c.duration_sec != null && <span>· {fmtDur(c.duration_sec)}</span>}
                                {c.status && <span className={`px-1.5 py-0.5 rounded-full ${c.status === 'approved' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-black/5 dark:bg-white/10'}`}>{c.status}</span>}
                              </div>
                            </div>
                            {selectedClip?.clip_id===c.clip_id && <Check size={16} className="text-[#FF3B30] flex-shrink-0" />}
                          </label>
                        ));
                      })()}
                    </div>
                  </>
                )}

                {creativeKind === 'upload' && (
                  <div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp"
                      onChange={handleFileChosen}
                      className="hidden"
                      data-testid="fanboost-upload-input"
                    />
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="w-full aspect-[9/16] max-w-[220px] rounded-xl border-2 border-dashed border-black/10 dark:border-white/10 flex flex-col items-center justify-center hover:border-[#FF3B30] transition"
                      data-testid="fanboost-upload-btn"
                    >
                      {uploading ? (
                        <><Loader2 size={22} className="animate-spin text-[#FF3B30] mb-2" /><span className="text-xs">업로드 중…</span></>
                      ) : (
                        <>
                          <UploadIcon size={22} className="text-gray-400 mb-2" />
                          <span className="text-xs font-bold">영상 또는 이미지 업로드</span>
                          <span className="text-[10px] text-gray-400 mt-1">MP4/MOV/WEBM · JPG/PNG/WEBP</span>
                          <span className="text-[10px] text-gray-400">영상 300MB · 이미지 5MB</span>
                        </>
                      )}
                    </button>
                    {uploaded && (
                      <p className="text-xs text-emerald-600 mt-2" data-testid="fanboost-upload-done">
                        <Check size={12} className="inline" /> {uploaded.media_type === 'video' ? '영상' : '이미지'} 업로드 완료
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Right column · 9:16 preview (desktop) · vertical stack on mobile */}
              <div className="flex flex-col items-center gap-2 md:pt-0 pt-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">9:16 미리보기</p>
                <CreativePreview
                  media_type={creative_source?.media_type}
                  video_url={creative_source?.video_url}
                  thumbnail_url={creative_source?.thumbnail_url}
                  embed_url={creative_source?.embed_url}
                  source_url={creative_source?.source_url}
                />
              </div>

              {/* Full-width row · interaction config for the selected mode */}
              <div className="md:col-span-2 mt-4 rounded-xl border border-black/5 dark:border-white/10 p-4 space-y-3" data-testid="fanboost-interaction-panel">
                <div className="flex items-center gap-2">
                  {mode === 'comment' ? <MessageCircle size={16} className="text-[#FF3B30]" /> : <MessageSquare size={16} className="text-[#FF3B30]" />}
                  <p className="text-sm font-black">{mode === 'comment' ? '댓글형 광고 설정' : '메시지형 광고 설정'}</p>
                  <button type="button" onClick={() => setExampleMode(mode)} className="ml-auto text-[11px] font-bold text-[#FF3B30] inline-flex items-center gap-1 hover:underline" data-testid="fanboost-interaction-example">
                    <Eye size={12} /> 예시 다시 보기
                  </button>
                </div>

                <label className="block text-sm">
                  <span className="font-bold">① {mode === 'comment' ? '광고 안내 문구' : '광고 CTA 안내 문구'}</span>
                  <textarea rows={2} value={interactionConfig.ad_prompt || ''}
                    onChange={(e) => setInteractionConfig((s) => ({ ...s, ad_prompt: e.target.value.slice(0, 200) }))}
                    className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                    placeholder={IC_PRESETS[mode]?.ad_prompt}
                    data-testid="fanboost-ic-ad-prompt" />
                  {!interactionConfig.ad_prompt && <p className="text-[10px] text-red-500 mt-0.5"><AlertCircle size={10} className="inline" /> 필수 입력</p>}
                </label>

                {mode === 'comment' && (
                  <label className="block text-sm">
                    <span className="font-bold">② 댓글 감지 키워드</span>
                    <input value={interactionConfig.comment_keyword || ''}
                      onChange={(e) => setInteractionConfig((s) => ({ ...s, comment_keyword: e.target.value.slice(0, 40) }))}
                      className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                      placeholder="예: 라이브모아"
                      data-testid="fanboost-ic-keyword" />
                    {!interactionConfig.comment_keyword && <p className="text-[10px] text-red-500 mt-0.5"><AlertCircle size={10} className="inline" /> 필수 입력</p>}
                  </label>
                )}
                {mode === 'message' && (
                  <label className="block text-sm">
                    <span className="font-bold">② 메시지 시작 문구</span>
                    <input value={interactionConfig.message_start_text || ''}
                      onChange={(e) => setInteractionConfig((s) => ({ ...s, message_start_text: e.target.value.slice(0, 200) }))}
                      className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                      placeholder="예: 다음 LIVE 알림을 받고 싶어요."
                      data-testid="fanboost-ic-msg-start" />
                    {!interactionConfig.message_start_text && <p className="text-[10px] text-red-500 mt-0.5"><AlertCircle size={10} className="inline" /> 필수 입력</p>}
                  </label>
                )}

                <label className="block text-sm">
                  <span className="font-bold">③ {mode === 'comment' ? '자동 DM 내용' : '자동 응답 내용'}</span>
                  <textarea rows={3} value={interactionConfig.auto_reply_text || ''}
                    onChange={(e) => setInteractionConfig((s) => ({ ...s, auto_reply_text: e.target.value.slice(0, 400) }))}
                    className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                    placeholder={IC_PRESETS[mode]?.auto_reply_text}
                    data-testid="fanboost-ic-auto-reply" />
                  {!interactionConfig.auto_reply_text && <p className="text-[10px] text-red-500 mt-0.5"><AlertCircle size={10} className="inline" /> 필수 입력</p>}
                </label>

                <label className="block text-sm">
                  <span className="font-bold">④ DM CTA 버튼 문구</span>
                  <input value={interactionConfig.cta_label || ''}
                    onChange={(e) => setInteractionConfig((s) => ({ ...s, cta_label: e.target.value.slice(0, 30) }))}
                    className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                    placeholder="예: 단골 등록하기"
                    data-testid="fanboost-ic-cta" />
                  {!interactionConfig.cta_label && <p className="text-[10px] text-red-500 mt-0.5"><AlertCircle size={10} className="inline" /> 필수 입력</p>}
                </label>

                <div className="text-[11px] text-gray-500 flex items-start gap-1.5 pt-1 border-t border-black/5 dark:border-white/10">
                  <ExternalLink size={12} className="mt-0.5 flex-shrink-0" />
                  <span>단골 등록 연결: <code className="text-[10px] bg-black/5 dark:bg-white/10 px-1 rounded">/g/{'{sellerId}'}?campaign={'{campaignId}'}</code> (기존 랜딩 REUSE)</span>
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 4 · Targeting ===== */}
          {step === 4 && (
            <div className="space-y-4 text-sm" data-testid="fanboost-target-panel">
              <div>
                <p className="font-bold mb-2">지역</p>
                <div className="flex flex-wrap gap-1.5" data-testid="fanboost-regions">
                  {REGIONS.map((r) => {
                    const isNationwide = r === NATIONWIDE;
                    const active = targeting.regions.includes(r);
                    return (
                      <button key={r} type="button"
                        onClick={() => setTargeting((t) => {
                          // Nationwide is mutually exclusive with any local region.
                          if (isNationwide) {
                            return { ...t, regions: active ? [] : [NATIONWIDE] };
                          }
                          const withoutNationwide = t.regions.filter((x) => x !== NATIONWIDE);
                          return {
                            ...t,
                            regions: withoutNationwide.includes(r)
                              ? withoutNationwide.filter((x) => x !== r)
                              : [...withoutNationwide, r],
                          };
                        })}
                        className={`px-3 py-1 rounded-full text-xs font-bold ${active ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'} ${isNationwide ? 'ring-1 ring-[#FF3B30]/30' : ''}`}
                        data-testid={isNationwide ? 'fanboost-region-nationwide' : `fanboost-region-${r}`}>
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="font-bold mb-2">연령</p>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="100" value={targeting.age_min} onChange={(e) => setTargeting((t)=>({ ...t, age_min: Number(e.target.value)||0 }))} className="w-20 px-2 py-1 border rounded text-sm bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="fanboost-age-min" />
                  <span>~</span>
                  <input type="number" min="0" max="100" value={targeting.age_max} onChange={(e) => setTargeting((t)=>({ ...t, age_max: Number(e.target.value)||0 }))} className="w-20 px-2 py-1 border rounded text-sm bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="fanboost-age-max" />
                  <span className="text-xs text-gray-400">세</span>
                </div>
              </div>
              <div>
                <p className="font-bold mb-2">성별</p>
                <div className="flex gap-2">
                  {GENDERS.map((g) => (
                    <button key={g.v} onClick={() => setTargeting((t)=>({ ...t, genders: [g.v] }))}
                      className={`px-3 py-1 rounded-full text-xs font-bold ${targeting.genders.includes(g.v) ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}
                      data-testid={`fanboost-gender-${g.v}`}>{g.l}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-bold mb-2">관심 카테고리</p>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map((c) => (
                    <button key={c} type="button"
                      onClick={() => setTargeting((t) => ({ ...t, categories: t.categories.includes(c) ? t.categories.filter(x=>x!==c) : [...t.categories, c] }))}
                      className={`px-3 py-1 rounded-full text-xs font-bold ${targeting.categories.includes(c) ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-gray-400">이 값은 내부 캠페인 설정입니다. 이번 단계에서는 Meta/TikTok 광고 API로 자동 전송되지 않습니다.</p>
            </div>
          )}

          {/* ===== STEP 5 · Budget & Schedule ===== */}
          {step === 5 && (
            <div className="space-y-3 text-sm" data-testid="fanboost-budget-panel">
              <label className="block">
                <span className="font-bold">캠페인 명 (선택)</span>
                <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 120))} placeholder="예: 봄 신상 패션 단골 늘리기" className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="fanboost-title" />
              </label>
              <label className="block">
                <span className="font-bold">총 예산 (원)</span>
                <input type="number" min="10000" step="10000" value={budget} onChange={(e) => setBudget(Number(e.target.value)||0)} className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="fanboost-budget" />
                <span className="text-[11px] text-gray-400">최소 10,000원 · 실제 결제는 승인 이후 별도 단계에서 진행됩니다.</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="font-bold">시작일</span>
                  <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="fanboost-start" />
                </label>
                <label className="block">
                  <span className="font-bold">종료일</span>
                  <input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="fanboost-end" />
                </label>
              </div>

              {/* Wallet snapshot · reuse only */}
              <div className="mt-3 p-3 rounded-xl bg-black/5 dark:bg-white/5" data-testid="fanboost-wallet">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold flex items-center gap-1.5"><Wallet size={14} /> 현재 잔액</span>
                  <span className="font-black">{wallet ? won(walletBalance) : '조회 중…'}</span>
                </div>
                <div className="flex items-center justify-between text-xs mt-1">
                  <span>예상 광고비</span>
                  <span className="font-bold">{won(estCost)}</span>
                </div>
                {insufficient && (
                  <p className="mt-2 text-[11px] text-red-500 flex items-center gap-1" data-testid="fanboost-insufficient">
                    <AlertCircle size={12} /> 잔액이 부족합니다. 승인 후 결제 단계에서 충전이 필요합니다.
                  </p>
                )}
                <p className="mt-1 text-[10px] text-gray-400">이번 단계에서는 실제 지갑 차감이 발생하지 않습니다.</p>
              </div>
            </div>
          )}

          {/* ===== STEP 6 · Preview ===== */}
          {step === 6 && (
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4" data-testid="fanboost-preview">
              <div className="flex flex-col items-center gap-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">9:16 미리보기</p>
                <CreativePreview
                  media_type={creative_source?.media_type}
                  video_url={creative_source?.video_url}
                  thumbnail_url={creative_source?.thumbnail_url}
                  embed_url={creative_source?.embed_url}
                  source_url={creative_source?.source_url}
                />
              </div>
              <div className="space-y-1 text-sm">
                <div className="p-3 rounded-lg bg-black/5 dark:bg-white/5 space-y-1">
                  <p><b>광고 방식:</b> {MODES.find(m=>m.value===mode)?.label}</p>
                  <p><b>플랫폼:</b> {PLATFORMS.find(p=>p.value===platform)?.label}</p>
                  <p><b>소재:</b> {creative_source?.kind === 'short_clip' ? `기존 숏폼 (${creative_source.ref_id})` : creative_source?.kind === 'short_clip_media' ? `업로드 영상 (${creative_source.ref_id})` : creative_source?.kind === 'upload' ? `업로드 이미지 (${creative_source.ref_id})` : '-'}</p>
                  <p><b>지역:</b> {targeting.regions.join(', ') || '전체'}</p>
                  <p><b>연령:</b> {targeting.age_min} ~ {targeting.age_max}세</p>
                  <p><b>성별:</b> {GENDERS.find(g=>targeting.genders.includes(g.v))?.l || '전체'}</p>
                  <p><b>카테고리:</b> {targeting.categories.join(', ') || '전체'}</p>
                  <p><b>예산:</b> {won(budget)}</p>
                  <p><b>기간:</b> {fmtDate(startAt)} ~ {fmtDate(endAt)}</p>
                </div>
                {/* STEP GROWTH-B2-FIX-4 · interaction summary */}
                <div className="p-3 rounded-lg bg-[#FF3B30]/5 border border-[#FF3B30]/20 space-y-1" data-testid="fanboost-preview-interaction">
                  <p className="font-black text-[#FF3B30] mb-1 flex items-center gap-1">
                    {mode === 'comment' ? <MessageCircle size={14} /> : <MessageSquare size={14} />}
                    {mode === 'comment' ? '댓글형 광고 설정' : '메시지형 광고 설정'}
                  </p>
                  <p><b>{mode === 'comment' ? '광고 안내:' : '광고 CTA:'}</b> <span className="line-clamp-2 inline">{interactionConfig.ad_prompt || '-'}</span></p>
                  {mode === 'comment' && <p><b>댓글 키워드:</b> {interactionConfig.comment_keyword || '-'}</p>}
                  {mode === 'message' && <p><b>메시지 시작:</b> <span className="line-clamp-1 inline">{interactionConfig.message_start_text || '-'}</span></p>}
                  <p><b>{mode === 'comment' ? '자동 DM:' : '자동 응답:'}</b> <span className="line-clamp-2 inline">{interactionConfig.auto_reply_text || '-'}</span></p>
                  <p><b>CTA:</b> {interactionConfig.cta_label || '-'}</p>
                </div>
                <div className="p-3 rounded-lg border border-dashed border-black/10 dark:border-white/10 text-xs text-gray-500">
                  승인 요청 후 관리자 검토를 거쳐 승인되면 <b>APPROVED</b> 상태가 됩니다.
                  이번 버전에서는 외부 광고 REAL 송출은 이루어지지 않고, LIVEMOA 단골 유입 랜딩 URL이 활성화됩니다.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-black/5 dark:border-white/10 sticky bottom-0 bg-white dark:bg-[#141414]">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm bg-black/5 dark:bg-white/10 disabled:opacity-40"
            data-testid="fanboost-prev"><ChevronLeft size={14} /> 이전</button>
          {step < 6 ? (
            <button onClick={() => canNext && setStep(step + 1)} disabled={!canNext}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold disabled:opacity-40"
              data-testid="fanboost-next">다음 <ChevronRight size={14} /></button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => save(false)} disabled={saving}
                className="px-3 py-2 rounded-full text-sm bg-black/5 dark:bg-white/10 font-bold"
                data-testid="fanboost-save-draft">초안 저장</button>
              <button onClick={() => save(true)} disabled={saving}
                className="px-4 py-2 rounded-full text-sm bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold"
                data-testid="fanboost-submit">승인 요청</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/* Section (renders inside AdsSection)                          */
/* ============================================================ */
export default function FanBoostSection({ sellerId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/seller/growth/fan-boost');
      setItems(r.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (ad_id) => {
    try {
      await api.post(`/seller/growth/fan-boost/${ad_id}/submit`);
      toast.success('승인 요청이 접수되었습니다.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
  };

  const copyLanding = async (ad_id) => {
    try {
      await navigator.clipboard.writeText(buildLandingUrl(sellerId, ad_id));
      toast.success('링크가 복사되었습니다.');
    } catch { toast.error('복사에 실패했습니다.'); }
  };

  return (
    <div className="mt-6 space-y-3" data-testid="fanboost-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-black">단골 늘리기 광고</h3>
          <p className="text-xs text-gray-500 mt-0.5">Instagram / TikTok 광고 유입 → 카카오 로그인 → 자동 단골 등록</p>
        </div>
        <button onClick={() => setWizardOpen(true)}
          className="px-3 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold"
          data-testid="fanboost-new">
          새 캠페인 만들기
        </button>
      </div>

      <FanBoostWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onCreated={load} />

      {loading ? (
        <p className="text-xs text-gray-400 py-6 text-center">불러오는 중…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-400 py-6 text-center" data-testid="fanboost-empty">등록된 단골 늘리기 캠페인이 없습니다.</p>
      ) : (
        <div className="divide-y divide-black/5 dark:divide-white/10 bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-xl" data-testid="fanboost-list">
          {items.map((c) => (
            <div key={c.ad_id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid={`fanboost-item-${c.ad_id}`}>
              {c.creative_source?.thumbnail_url && (
                <img src={c.creative_source.thumbnail_url} alt="" className="w-10 h-16 rounded object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${STATUS_TONE[c.status] || 'bg-black/5'}`}>
                    {STATUS_LABEL[c.status] || c.status}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-black/5 dark:bg-white/10 uppercase">{c.platform || '-'}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-black/5 dark:bg-white/10">{c.mode === 'comment' ? '댓글형' : '메시지형'}</span>
                </div>
                <p className="mt-1 text-sm font-bold truncate">{c.title || '단골 늘리기 광고'}</p>
                <p className="text-[11px] text-gray-500">
                  {won(c.budget_krw)} · {c.start_at ? new Date(c.start_at).toLocaleDateString('ko-KR') : '-'} ~ {c.end_at ? new Date(c.end_at).toLocaleDateString('ko-KR') : '-'}
                </p>
                {c.rejection_reason && <p className="text-[11px] text-red-500 mt-0.5">반려 사유: {c.rejection_reason}</p>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {c.status === 'draft' && (
                  <button onClick={() => submit(c.ad_id)}
                    className="px-2.5 py-1 rounded-full text-xs bg-[#FF3B30] text-white font-bold"
                    data-testid={`fanboost-submit-${c.ad_id}`}>승인 요청</button>
                )}
                {(c.status === 'approved' || c.status === 'running') && (
                  <>
                    <a href={buildLandingUrl(sellerId, c.ad_id)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-black/5 dark:bg-white/10 font-bold"
                      data-testid={`fanboost-open-${c.ad_id}`}>
                      <ExternalLink size={12} /> 열기
                    </a>
                    <button onClick={() => copyLanding(c.ad_id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-black/5 dark:bg-white/10 font-bold"
                      data-testid={`fanboost-copy-${c.ad_id}`}>
                      <Copy size={12} /> 링크 복사
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
